(ns leg.nav
  "The one row of controls, and it is at the foot of every screen.

  A phone is held at the bottom. The thumb rests over the bottom edge and
  reaches the top of a six-inch screen only by shifting the grip - so the
  controls are down there, on every screen and not only in an article, and
  there is nothing along the top edge to reach for at all. What used to be up
  there is either in this bar now or gone: the way back to the feeds, the name
  of what is open, the feed's own button.

  [Home] is the leftmost thing on the screen's bottom edge, always, and it
  always means the same: every feed, nothing open, the list. A button that is
  in one place on every screen and does the same thing from all of them is the
  way out of anywhere - which in an installed app, where there is no address
  bar and no browser chrome, is not a convenience.

  Once per screen, though, and not once per pane: a wide window is three panes
  side by side with three bars, and the leftmost of them is the one that
  carries it. Three [Home]s in a row would be three ways to a place two thirds
  of which is already on the screen.

  `Bar` is the frame and the screen fills it: the rule, the paper, the space a
  phone's own bar takes, and buttons small enough that a row of them fits a
  narrow screen. What goes in is the screen's own business, because what can
  be done depends on what is being looked at."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [leg.mui :as mui :refer [sx]]
            [leg.state :as state]))

(def-ui Side
  "One end of the bar. Both ends are a column of the grid and both columns
  are `1fr`, so they are the same width whatever is in them - which is what
  puts the middle in the middle."
  [align ***]
  ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :gap ".2rem"
                       :justifyContent align
                       ;; A column that may not push the other two aside.
                       :minWidth 0})}
     ***))

(def-ui Bar
  "The frame: one row, at the bottom, over the phone's own bar.

  Three parts, and they are given as `left`, `middle` and `right` rather than
  poured in as children, because the middle has to *be* in the middle: the
  row is a grid of `1fr auto 1fr`, so both ends get the same width and what
  is between them sits on the centre line of the screen no matter how many
  words each end has. `left` is where the window is going, `right` is what
  can be done to what is shown, `middle` is what is pressed over and over -
  which on a phone belongs where the thumb already rests.

  A `nav` and labelled, because it is the navigation of this screen and a
  screen reader should be able to jump to it - and every screen has exactly
  one, so the label is what says which screen it belongs to."
  [label left middle right gap]
  ($ mui/Box
     {:component "nav"
      :aria-label label
      :sx (sx {:flex "none"
               :containerType "inline-size"
               :display "grid" :gridTemplateColumns "1fr auto 1fr"
               :alignItems "center" :gap ".2rem"
               :padding ".3rem .4rem"
               ;; The phone's own bar sits under this one.
               :paddingBottom "max(.3rem, env(safe-area-inset-bottom))"
               :borderTop "1px solid" :borderColor "divider"
               :backgroundColor "background.paper"
               ;; Everything in here is a word on a screen that may be 360
               ;; pixels wide, and the widest of these rows holds six. Said
               ;; once, here, rather than on every button in four files.
               "& .MuiButton-root" {:minWidth 0 :fontSize ".8rem"
                                    :whiteSpace "nowrap"
                                    :paddingLeft ".35rem" :paddingRight ".35rem"}
               ;; Except the ones that are a shape rather than a word - the
               ;; arrows, the star, the read dot (`data-glyph`). A glyph set
               ;; at the size of a label is a smudge, and a word's size is
               ;; set by the row it is in while a shape's is set by the thumb
               ;; that has to hit it. Here and not in their own `sx`, because
               ;; the rule above is a descendant selector and would win
               ;; against one: anything a button says about itself from
               ;; inside this bar has to be said here to be heard.
               "& .MuiButton-root[data-glyph]" {:fontSize "1.15rem"}
               ;; Where the row is narrow there are six words in it and 390
               ;; pixels to put them in. A word smaller rather than a word
               ;; broken over two lines: `Mark read` wrapped, and a bar that
               ;; is two lines tall on one screen and one on the next is a bar
               ;; that moves under the thumb. The buttons stay as tall as they
               ;; were - what is touched is the button, not the word.
               ;;
               ;; Asked of this bar and not of the window (`@container`, with
               ;; the `containerType` above): the bar of the article list is
               ;; 24rem wide in a 1280-pixel window, so what matters is how
               ;; much room the row has, not how big the screen is.
               "@container (max-width: 26rem)"
               {"& .MuiButton-root" {:fontSize ".72rem"
                                     :paddingLeft ".25rem" :paddingRight ".25rem"}
                "& .MuiButton-root[data-glyph]" {:fontSize "1.05rem"}}
               ;; And the one that was pressed and is being waited on goes
               ;; on moving until what it asked for is there
               ;; (`leg.state/refresh!` sets `aria-busy`, and the
               ;; keyframe is in `index.html`). Everything in this row is
               ;; disabled while a fetch runs, and grey and still is what
               ;; a button that cannot be pressed looks like whether or
               ;; not anything is happening - so the one that *is* doing
               ;; something takes its ink back and breathes. The progress
               ;; bar says the same thing at the top of the list, which on
               ;; a phone is the end of the screen the thumb is not at.
               "& .MuiButton-root[aria-busy=\"true\"].Mui-disabled"
               {:color "text.secondary"
                :animation "leser-breathe 1.2s ease-in-out infinite"}
               ;; Somebody who asked for no motion gets none, and still
               ;; gets the answer: the word stays dark while the rest of
               ;; the row is grey, which is the same sentence said
               ;; without moving.
               "@media (prefers-reduced-motion: reduce)"
               {"& .MuiButton-root[aria-busy=\"true\"].Mui-disabled"
                {:animation "none"}}
               "@media (pointer: coarse)" {"& .MuiButton-root" {:minHeight "2.5rem"}}})}
     ($ Side {:align "flex-start"} left)
     ;; `gap` is the point of the middle as much as the position is: two
     ;; arrows a thumb-width apart are two buttons, two arrows next to each
     ;; other are one button somebody keeps hitting the wrong half of.
     ($ mui/Box {:sx (sx {:display "flex" :alignItems "center"
                          :gap (or gap ".4rem")})}
        middle)
     ($ Side {:align "flex-end"} right)))

(def-ui Filters
  "Which of a list is shown, as the words themselves rather than a control
  with a name: two or three words, the one that is on in the accent and the
  rest in the second ink. A select would be one word and a press to find
  out what the others are, and there is room for all of them.

  It lives in the middle of the bar, where a thumb rests, because it is
  what gets pressed over and over - and here rather than in either list,
  because both of them have one now: the articles of a feed, and the names
  of the feeds."
  [current options on-change]
  ($ mui/Box {:sx (sx {:display "flex" :gap ".25rem"})}
     (for [[k label] options]
       ($ mui/Button {:key (name k)
                      :onClick (fn [_] (on-change k))
                      :color (if (= k current) "primary" "inherit")
                      :sx (sx (when-not (= k current) {:color "text.secondary"}))}
          label))))

(def-ui Home
  "The way out of anywhere: the front page, which is every feed and nothing
  open.

  Asked of the window rather than stored, because the front of a narrow
  window is the list of feeds and the front of a wide one is all three
  columns at once - the same place through a different window (see
  `leg.route`)."
  []
  (let [narrow? (mui/use-narrow?)]
    ($ mui/Button {:onClick (fn [_] (state/home! narrow?))} "Home")))
